import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings, GitBranch, Zap, ClipboardList, Plus, Trash2, Activity,
  Copy, Columns, Unlink
} from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import TasksEmbedCodeDialog from './TasksEmbedCodeDialog';
import type { BoardWorkflowStat, BoardWorkflowRun, BoardWorkflowNode } from '@easyflow/shared';

/** Board editabile nel modal (subset di Board, campi opzionali in create). */
export interface BoardDraft {
  id?: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  board_type?: string;
  workgroup_id?: string | null;
  is_template?: boolean;
}

/** Utente selezionabile come membro/watcher board. */
interface BoardUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  [key: string]: unknown;
}

/** Membro di un gruppo di lavoro (GET /messenger/workgroups/:id). */
interface WorkgroupMember {
  user_id: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  [key: string]: unknown;
}

/** Gruppo di lavoro (GET /messenger/workgroups). */
interface Workgroup {
  id: string;
  name?: string;
  member_count?: number | string;
  [key: string]: unknown;
}

/** Automazione dal catalogo (/tasks/automation-rules). */
interface AutomationCatalogItem {
  id: string;
  name?: string;
  [key: string]: unknown;
}

/** Link automazione a board (/tasks/boards/:id/automation-links). */
interface BoardAutomationLink {
  id?: string;
  automation_rule_id?: string;
  rule_name?: string;
  name?: string;
  is_active?: boolean;
  trigger_type?: string;
  action_type?: string;
  [key: string]: unknown;
}

/** Form dal catalogo (/tasks/embed-forms). */
interface FormCatalogItem {
  id: string;
  name?: string;
  [key: string]: unknown;
}

/** Link form a board (/tasks/boards/:id/form-links). */
interface BoardFormLink {
  id?: string;
  form_id?: string;
  form_name?: string;
  name?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

interface BoardStatus {
  id: string;
  board_id: string;
  name: string;
  color: string;
  icon?: string;
  position: number;
  is_default: boolean;
  is_completed: boolean;
  is_cancelled: boolean;
}

export interface BoardCreateInput {
  name: string;
  description?: string;
  color?: string;
  board_type: string;
  is_template: boolean;
  workgroup_id: string | null;
}

interface BoardSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit';
  board: BoardDraft | null;
  boardStatuses: BoardStatus[];
  boardWorkflowStats: BoardWorkflowStat[];
  boardWorkflowRuns: BoardWorkflowRun[];
  users: BoardUser[];
  existingBoardNames?: string[];
  onSave: (board: BoardDraft | null) => void;
  onCreate?: (data: BoardCreateInput) => void;
  onDeleteBoard: (boardId: string) => void;
  onAddStatus: (name: string, color: string) => void;
  onDeleteStatus: (statusId: string) => void;
  onOpenAddWorkflow: () => void;
  onToggleWorkflow: (triggerId: string, isActive: boolean) => void;
  onRemoveWorkflow: (triggerId: string, name: string) => void;
  getEmbedCode: (formId: string) => string;
}

export default function BoardSettingsModal({
  open, onOpenChange, mode = 'edit', board, boardStatuses,
  boardWorkflowStats, boardWorkflowRuns, users, existingBoardNames = [],
  onSave, onCreate, onDeleteBoard, onAddStatus, onDeleteStatus,
  onOpenAddWorkflow, onToggleWorkflow, onRemoveWorkflow, getEmbedCode,
}: BoardSettingsModalProps) {
  const isCreate = mode === 'create';
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('general');
  const [editBoard, setEditBoard] = useState<BoardDraft | null>(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#6366f1');
  const [boardType, setBoardType] = useState<string>('project');
  const [isTemplate, setIsTemplate] = useState(false);

  const [workgroups, setWorkgroups] = useState<Workgroup[]>([]);
  const [selectedWorkgroupId, setSelectedWorkgroupId] = useState<string>('');
  const [workgroupMembers, setWorkgroupMembers] = useState<WorkgroupMember[]>([]);

  const [linkedAutomations, setLinkedAutomations] = useState<BoardAutomationLink[]>([]);
  const [allAutomations, setAllAutomations] = useState<AutomationCatalogItem[]>([]);
  const [selectedAutomationId, setSelectedAutomationId] = useState('');

  const [linkedForms, setLinkedForms] = useState<BoardFormLink[]>([]);
  const [allForms, setAllForms] = useState<FormCatalogItem[]>([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedDialogFormName, setEmbedDialogFormName] = useState<string>('');
  const [embedDialogCode, setEmbedDialogCode] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    if (board) {
      setEditBoard({ ...board });
      setBoardType(board.board_type || 'project');
      setIsTemplate(board.is_template || false);
      setSelectedWorkgroupId(board.workgroup_id || '');
    } else if (isCreate) {
      setEditBoard({ name: '', description: '', color: '#6366f1', icon: 'clipboard-list' });
      setBoardType('team');
      setIsTemplate(false);
      setSelectedWorkgroupId('');
      setActiveTab('general');
    }
  }, [board, open, isCreate]);

  const loadWorkgroups = useCallback(async () => {
    try {
      const res = await apiClient.get<Array<{ id: string; name?: string; member_count?: number | string; [key: string]: unknown }>>('/messenger/workgroups');
      if (res.success) setWorkgroups(res.data || []);
    } catch {}
  }, []);

  const loadLinkedAutomations = useCallback(async () => {
    if (!board?.id) return;
    try {
      const res = await apiClient.get<Array<{ id?: string; automation_rule_id?: string; rule_name?: string; name?: string; is_active?: boolean; trigger_type?: string; action_type?: string; [key: string]: unknown }>>(`/tasks/boards/${board.id}/automation-links`);
      if (res.success) setLinkedAutomations(res.data || []);
    } catch {}
  }, [board?.id]);

  const loadLinkedForms = useCallback(async () => {
    if (!board?.id) return;
    try {
      const res = await apiClient.get<Array<{ id?: string; form_id?: string; form_name?: string; name?: string; is_active?: boolean; [key: string]: unknown }>>(`/tasks/boards/${board.id}/form-links`);
      if (res.success) setLinkedForms(res.data || []);
    } catch {}
  }, [board?.id]);

  const loadAllCatalog = useCallback(async () => {
    try {
      const [rulesRes, formsRes] = await Promise.all([
        apiClient.get<Array<{ id: string; name?: string; [key: string]: unknown }>>('/tasks/automation-rules'),
        apiClient.get<Array<{ id: string; name?: string; [key: string]: unknown }>>('/tasks/embed-forms'),
      ]);
      if (rulesRes.success) setAllAutomations(rulesRes.data || []);
      if (formsRes.success) setAllForms(formsRes.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    loadWorkgroups();
    if (board?.id) {
      loadLinkedAutomations();
      loadLinkedForms();
      loadAllCatalog();
    }
  }, [open, board?.id, loadAllCatalog, loadLinkedAutomations, loadLinkedForms, loadWorkgroups]);

  const loadWorkgroupMembers = useCallback(async (wgId: string) => {
    try {
      const res = await apiClient.get<{ members?: Array<{ user_id: string; first_name?: string; last_name?: string; role?: string; [key: string]: unknown }> }>(`/messenger/workgroups/${wgId}`);
      if (res.success && res.data?.members) {
        setWorkgroupMembers(res.data.members);
      }
    } catch {
      setWorkgroupMembers([]);
    }
  }, []);

  useEffect(() => {
    if (selectedWorkgroupId) {
      loadWorkgroupMembers(selectedWorkgroupId);
    } else {
      setWorkgroupMembers([]);
    }
  }, [selectedWorkgroupId, loadWorkgroupMembers]);

  const handleLinkAutomation = async () => {
    if (!selectedAutomationId || !board?.id) return;
    try {
      await apiClient.post(`/tasks/boards/${board.id}/automation-links`, { automation_rule_id: selectedAutomationId });
      toast({ title: 'Automazione collegata' });
      setSelectedAutomationId('');
      loadLinkedAutomations();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleUnlinkAutomation = async (ruleId: string) => {
    if (!board?.id) return;
    try {
      await apiClient.delete(`/tasks/boards/${board.id}/automation-links/${ruleId}`);
      toast({ title: 'Automazione scollegata' });
      loadLinkedAutomations();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleLinkForm = async () => {
    if (!selectedFormId || !board?.id) return;
    try {
      await apiClient.post(`/tasks/boards/${board.id}/form-links`, { form_id: selectedFormId });
      toast({ title: 'Form collegato' });
      setSelectedFormId('');
      loadLinkedForms();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleUnlinkForm = async (formId: string) => {
    if (!board?.id) return;
    try {
      await apiClient.delete(`/tasks/boards/${board.id}/form-links/${formId}`);
      toast({ title: 'Form scollegato' });
      loadLinkedForms();
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleSaveBoardType = async () => {
    if (!board?.id) return;
    try {
      await apiClient.put(`/tasks/boards/${board.id}/type`, { board_type: boardType });
      toast({ title: 'Tipo board aggiornato' });
    } catch { toast({ title: 'Errore', variant: 'destructive' }); }
  };

  const handleSave = () => {
    if (editBoard) {
      if (boardType !== (board?.board_type || 'project')) {
        handleSaveBoardType();
      }
      const workgroupId = selectedWorkgroupId || null;
      onSave({ ...editBoard, is_template: isTemplate, workgroup_id: workgroupId });
    }
  };

  const trimmedName = (editBoard?.name || '').trim();
  const isDuplicateName = isCreate && trimmedName.length > 0 && existingBoardNames.includes(trimmedName.toLowerCase());

  const handleCreate = () => {
    if (!isCreate || !onCreate || !trimmedName || isDuplicateName || !editBoard) return;
    onCreate({
      name: trimmedName,
      description: editBoard.description?.trim() || undefined,
      color: editBoard.color || '#6366f1',
      board_type: boardType,
      is_template: isTemplate,
      workgroup_id: selectedWorkgroupId || null,
    });
  };

  if (!editBoard) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Nuova Board' : 'Impostazioni Board'}</DialogTitle>
        </DialogHeader>
        <Tabs value={isCreate ? 'general' : activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex-wrap h-auto">
            <TabsTrigger value="general" className="flex-1"><Settings className="w-3.5 h-3.5 mr-1" />Generale</TabsTrigger>
            <TabsTrigger value="statuses" className="flex-1" disabled={isCreate}><Columns className="w-3.5 h-3.5 mr-1" />Stati/Colonne</TabsTrigger>
            <TabsTrigger value="workflows" className="flex-1" disabled={isCreate}><GitBranch className="w-3.5 h-3.5 mr-1" />Workflows</TabsTrigger>
            <TabsTrigger value="automations" className="flex-1" disabled={isCreate}><Zap className="w-3.5 h-3.5 mr-1" />Automazioni</TabsTrigger>
            <TabsTrigger value="forms" className="flex-1" disabled={isCreate}><ClipboardList className="w-3.5 h-3.5 mr-1" />Form</TabsTrigger>
          </TabsList>
          {isCreate && (
            <p className="text-[11px] text-muted-foreground mt-2 px-0.5">
              Stati, Workflows, Automazioni e Form si configurano dopo aver creato la board.
            </p>
          )}

          <TabsContent value="general">
            {editBoard && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Nome{isCreate ? ' *' : ''}</Label>
                  <Input value={editBoard.name} onChange={e => setEditBoard((prev) => prev ? { ...prev, name: e.target.value } : null)} className="mt-1" placeholder="Es. Sviluppo, Marketing, HR..." />
                  {isDuplicateName && (
                    <p className="text-xs text-red-500 mt-1">Esiste già una board con questo nome</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Descrizione</Label>
                  <Input value={editBoard.description || ''} onChange={e => setEditBoard((prev) => prev ? { ...prev, description: e.target.value } : null)} className="mt-1" placeholder="Descrizione opzionale..." />
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <Label className="text-xs">Colore</Label>
                    <Input type="color" value={editBoard.color || '#6366f1'} onChange={e => setEditBoard((prev) => prev ? { ...prev, color: e.target.value } : null)} className="mt-1 h-8 w-16" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Tipo Board</Label>
                    <Select value={boardType} onValueChange={setBoardType}>
                      <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal">Personale</SelectItem>
                        <SelectItem value="team">Team</SelectItem>
                        <SelectItem value="project">Progetto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div>
                    <Label className="text-xs">Board Template</Label>
                    <p className="text-[10px] text-muted-foreground">Rendi questa board disponibile come template clonabile</p>
                  </div>
                  <Switch checked={isTemplate} onCheckedChange={setIsTemplate} />
                </div>

                <Separator />

                <div>
                  <Label className="text-xs font-semibold">Gruppo di Lavoro</Label>
                  <p className="text-xs text-muted-foreground mt-1">Collega la board a un Gruppo di Lavoro per gestire visibilità e accesso</p>
                  <Select value={selectedWorkgroupId || '__none__'} onValueChange={(v) => setSelectedWorkgroupId(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="mt-2 h-8 text-sm"><SelectValue placeholder="Nessun gruppo (visibile a tutti)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessun gruppo (visibile a tutti)</SelectItem>
                      {workgroups.map(wg => (
                        <SelectItem key={wg.id} value={wg.id}>
                          {wg.name} ({wg.member_count} membri)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedWorkgroupId && workgroupMembers.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Membri del gruppo ({workgroupMembers.length})</Label>
                    <div className="mt-2 space-y-1 max-h-[150px] overflow-y-auto">
                      {workgroupMembers.map((m) => (
                        <div key={m.user_id} className="flex items-center gap-2 py-1 px-2 rounded bg-muted/50">
                          <div className="w-6 h-6 rounded-full bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-bold">
                            {(m.first_name || '?').charAt(0).toUpperCase()}
                          </div>
                          <span className="text-xs flex-1">{m.first_name} {m.last_name}</span>
                          <Badge variant="outline" className="text-[9px]">{m.role}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                {!isCreate && (
                  <Button variant="destructive" size="sm" className="w-full" onClick={() => { if (editBoard.id) onDeleteBoard(editBoard.id); onOpenChange(false); }}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina Board
                  </Button>
                )}
              </div>
            )}
            <DialogFooter className="mt-4 sm:gap-3 sm:space-x-0">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              {isCreate
                ? <Button onClick={handleCreate} disabled={!trimmedName || isDuplicateName}>Crea Board</Button>
                : <Button onClick={handleSave}>Salva</Button>}
            </DialogFooter>
          </TabsContent>

          <TabsContent value="statuses">
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">Pipeline Stati</Label>
                <p className="text-xs text-muted-foreground mt-1">Configura gli stati/colonne della pipeline di questa board</p>
              </div>
              <div className="space-y-1">
                {boardStatuses.map(s => (
                  <div key={s.id} className="flex items-center gap-2 py-1.5 px-2 border rounded-lg">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="text-sm flex-1">{s.name}</span>
                    {s.is_default && <Badge variant="outline" className="text-[9px]">Default</Badge>}
                    {s.is_completed && <Badge className="text-[9px] bg-green-500/15 text-green-700 dark:text-green-300 border-0">Completato</Badge>}
                    {s.is_cancelled && <Badge className="text-[9px] bg-red-500/15 text-red-700 dark:text-red-300 border-0">Annullato</Badge>}
                    {!s.is_default && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onDeleteStatus(s.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input type="color" value={newStatusColor} onChange={e => setNewStatusColor(e.target.value)} className="h-8 w-10 p-0.5" />
                <Input value={newStatusName} onChange={e => setNewStatusName(e.target.value)} placeholder="Nuovo stato..." className="h-8 text-sm flex-1" onKeyDown={e => { if (e.key === 'Enter' && newStatusName.trim()) { onAddStatus(newStatusName, newStatusColor); setNewStatusName(''); } }} />
                <Button size="sm" onClick={() => { onAddStatus(newStatusName, newStatusColor); setNewStatusName(''); }} disabled={!newStatusName.trim()} className="h-8"><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="workflows">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Workflow Builder collegati a questa board</p>
                <Button size="sm" onClick={onOpenAddWorkflow}>
                  <Plus className="w-3.5 h-3.5 mr-1" />Collega Workflow
                </Button>
              </div>

              {boardWorkflowStats.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nessun workflow collegato</p>
                  <p className="text-xs mt-1">Collega un workflow per automatizzare le azioni di questa board</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {boardWorkflowStats.map((stat) => {
                    const actionNodes = Array.isArray(stat.nodes) ? stat.nodes.filter((n) => n.type !== 'trigger' && n.type !== 'start') : [];
                    return (
                      <div key={stat.trigger_id} className="border rounded-lg p-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Switch checked={!!stat.is_active} onCheckedChange={() => onToggleWorkflow(stat.trigger_id, !!stat.is_active)} />
                            <span className={`text-sm font-medium truncate ${!stat.is_active ? 'text-muted-foreground line-through' : ''}`}>{stat.workflow_name}</span>
                          </div>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => onRemoveWorkflow(stat.trigger_id, stat.workflow_name ?? '')}>
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-[10px] bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30">
                            {stat.trigger_type || 'trigger'}
                          </Badge>
                          {actionNodes.slice(0, 3).map((n: BoardWorkflowNode, i: number) => (
                            <Badge key={i} variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">{n.data?.label || n.type}</Badge>
                          ))}
                          {actionNodes.length > 3 && <span className="text-[10px] text-muted-foreground">+{actionNodes.length - 3}</span>}
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                          <span>Esecuzioni: <strong>{stat.total_runs || 0}</strong></span>
                          {Number(stat.success_count) > 0 && <span className="text-green-600 dark:text-green-300">{stat.success_count} ok</span>}
                          {Number(stat.failed_count) > 0 && <span className="text-red-600 dark:text-red-400">{stat.failed_count} err</span>}
                          {Number(stat.running_count) > 0 && <span className="text-blue-600 dark:text-blue-300">{stat.running_count} in corso</span>}
                          <button className="text-purple-600 dark:text-purple-300 hover:underline" onClick={() => navigate('../workflow-builder')}>Builder</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {boardWorkflowRuns.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-foreground mb-2">Ultime esecuzioni</p>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {boardWorkflowRuns.map((run) => (
                      <div key={run.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded bg-muted/50">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${run.status === 'completed' ? 'bg-green-500' : run.status === 'failed' ? 'bg-red-500' : run.status === 'running' ? 'bg-blue-500' : 'bg-muted-foreground/30'}`} />
                        <span className="font-medium truncate flex-1">{run.workflow_name}</span>
                        <Badge variant="outline" className="text-[10px]">{run.trigger_type || 'trigger'}</Badge>
                        <span className="text-muted-foreground shrink-0">{run.started_at ? new Date(run.started_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="automations">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Collega automazioni dal catalogo centralizzato a questa board</p>
              </div>

              {linkedAutomations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nessuna automazione collegata</p>
                  <p className="text-xs mt-1">Seleziona dal catalogo per attivare automazioni su questa board</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {linkedAutomations.map((link) => (
                    <div key={link.automation_rule_id || link.id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-yellow-500 shrink-0" />
                          <span className="text-sm font-medium">{link.rule_name || link.name || 'Automazione'}</span>
                          {link.is_active ? (
                            <Badge variant="outline" className="text-[10px] h-4 bg-green-500/10 dark:bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30">Attivo</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] h-4 bg-muted/50 text-muted-foreground border-border">Disattivo</Badge>
                          )}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleUnlinkAutomation(link.automation_rule_id || link.id || '')}>
                              <Unlink className="w-3 h-3 text-orange-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Scollega da questa board</TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {link.trigger_type && (
                          <Badge variant="secondary" className="text-[10px]">
                            <Activity className="w-3 h-3 mr-1" />
                            {({ task_created: 'Creazione task', status_changed: 'Cambio stato', task_assigned: 'Assegnazione', due_date_approaching: 'Scadenza vicina' } as Record<string, string>)[link.trigger_type] || link.trigger_type}
                          </Badge>
                        )}
                        {link.action_type && (
                          <Badge variant="secondary" className="text-[10px]">
                            <Zap className="w-3 h-3 mr-1" />
                            {({ send_notification: 'Notifica', send_whatsapp: 'WhatsApp', send_sms: 'SMS', send_email: 'Email', change_status: 'Cambia stato', assign_user: 'Assegna utente', add_tag: 'Aggiungi tag', create_comment: 'Commento' } as Record<string, string>)[link.action_type] || link.action_type}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Separator />
              <div className="flex items-center gap-2">
                <Select value={selectedAutomationId} onValueChange={setSelectedAutomationId}>
                  <SelectTrigger className="flex-1 h-8 text-sm"><SelectValue placeholder="Seleziona automazione dal catalogo..." /></SelectTrigger>
                  <SelectContent>
                    {allAutomations.filter(a => !linkedAutomations.find((la) => (la.automation_rule_id || la.id) === a.id)).map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8" onClick={handleLinkAutomation} disabled={!selectedAutomationId}>
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">Le automazioni si gestiscono dal catalogo nelle Impostazioni Task</p>
            </div>
          </TabsContent>

          <TabsContent value="forms">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Collega form dal catalogo centralizzato a questa board</p>
              </div>

              {linkedForms.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Nessun form collegato</p>
                  <p className="text-xs mt-1">Seleziona dal catalogo per collegare form a questa board</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {linkedForms.map((link) => (
                    <div key={link.form_id || link.id} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="w-4 h-4 text-blue-500 shrink-0" />
                          <span className="text-sm font-medium">{link.form_name || link.name || 'Form'}</span>
                          {link.is_active ? (
                            <Badge variant="outline" className="text-[10px] h-4 bg-green-500/10 dark:bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30">Attivo</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] h-4 bg-muted/50 text-muted-foreground border-border">Disattivo</Badge>
                          )}
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleUnlinkForm(link.form_id || link.id || '')}>
                              <Unlink className="w-3 h-3 text-orange-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Scollega da questa board</TooltipContent>
                        </Tooltip>
                      </div>
                      {link.form_id && (
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => {
                            setEmbedDialogFormName(link.form_name || link.name || 'Form');
                            setEmbedDialogCode(getEmbedCode(link.form_id ?? ''));
                            setEmbedDialogOpen(true);
                          }}>
                            <Copy className="w-3 h-3 mr-1" />Codice Embed
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Separator />
              <div className="flex items-center gap-2">
                <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                  <SelectTrigger className="flex-1 h-8 text-sm"><SelectValue placeholder="Seleziona form dal catalogo..." /></SelectTrigger>
                  <SelectContent>
                    {allForms.filter(f => !linkedForms.find((lf) => (lf.form_id || lf.id) === f.id)).map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8" onClick={handleLinkForm} disabled={!selectedFormId}>
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">I form si gestiscono dal catalogo nelle Impostazioni Task</p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    <TasksEmbedCodeDialog
      open={embedDialogOpen}
      onOpenChange={setEmbedDialogOpen}
      formName={embedDialogFormName}
      embedCode={embedDialogCode}
    />
    </>
  );
}
